
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { stripePromise } from '@/integrations/stripe/client';
import { isItemClosed } from '@/contexts/CartContext';

export const useCheckout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const createCheckoutSession = async (items: any[], customerInfo: any, bookingData?: any, promoCode?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setDebugInfo(null);

      console.log('Creating checkout session with items:', items);
      console.log('Customer info:', customerInfo);
      console.log('Booking data:', bookingData);

      // Block checkout if any item belongs to a temporarily closed area
      if (Array.isArray(items) && items.some(item => isItemClosed(item))) {
        throw new Error(
          'One or more items in your cart are temporarily closed due to wildfire activity and cannot be purchased. Please remove them and try again.'
        );
      }


      // Check if Stripe is available
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe is not available. Please check that your Stripe publishable key is configured.');
      }

      // Validate customer info before proceeding
      if (!customerInfo || !customerInfo.email || !customerInfo.name) {
        throw new Error('Customer information is required (email and name)');
      }

      // Ensure customer info is properly structured with all collected fields
      const validatedCustomerInfo = {
        email: customerInfo.email.trim(),
        name: customerInfo.name.trim(),
        phone: customerInfo.phone?.trim() || '',
        address: customerInfo.address?.trim() || '',
        city: customerInfo.city?.trim() || '', 
        state: customerInfo.state?.trim() || '',
        zipCode: customerInfo.zipCode?.trim() || '',
        licensePlate: customerInfo.licensePlate?.trim() || ''
      };

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(validatedCustomerInfo.email)) {
        throw new Error('Please provide a valid email address');
      }

      // Validate name
      if (validatedCustomerInfo.name.length < 2) {
        throw new Error('Please provide a valid name (at least 2 characters)');
      }

      console.log('Validated customer info:', validatedCustomerInfo);

      // Determine item type based on actual content of all items
      const hasBookings = items.some(item => item.type === 'rv-site' || item.campType === 'rv' || item.siteId);
      const hasPermits = items.some(item => item.type === 'permit' || item.type === 'fishing');
      
      let itemType = 'unknown';
      if (hasBookings && hasPermits) {
        itemType = 'mixed';
      } else if (hasBookings) {
        itemType = 'rv-site';
      } else if (hasPermits) {
        itemType = 'permits';
      }

      console.log('Determined item type:', itemType);

      // Add permit IDs if any
      const permitIds = items
        .filter(item => item.permitId)
        .map(item => item.permitId);

      // Prepare the request payload with complete customer information
      const requestPayload = {
        items,
        customerInfo: validatedCustomerInfo,
        promoCode: promoCode || null,
        returnUrl: window.location.origin
      };

      console.log('Final request payload:', requestPayload);

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: requestPayload
      });

      if (error) {
        console.error('Error creating checkout session:', error);
        setDebugInfo({ error, items, customerInfo: validatedCustomerInfo, requestPayload });
        throw new Error(error.message || 'Failed to create checkout session');
      }

      console.log('Checkout session created successfully:', data);

      // Log the exact response structure to debug any issues
      console.log('[checkout] server response data:', JSON.stringify(data, null, 2));

      if (!data?.sessionId && !data?.url) {
        throw new Error('No session ID or URL returned from checkout creation');
      }

      // NOTE: track-verification call removed — the webhook handles all
      // payment processing via process-payment-wrapper. The frontend polls
      // payment_verifications directly for status.

      // Use the URL for redirect if available, otherwise use redirectToCheckout
      if (data.url) {
        console.log('Redirecting to Stripe checkout URL:', data.url);
        window.location.href = data.url;
      } else if (data.sessionId) {
        console.log('Using Stripe redirectToCheckout with session ID:', data.sessionId);
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });

        if (stripeError) {
          throw new Error(stripeError.message || 'Failed to redirect to checkout');
        }
      } else {
        throw new Error('No valid redirect method available');
      }

    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setDebugInfo({ error: err, items, customerInfo, bookingData });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Alias for backward compatibility
  const handleCheckout = createCheckoutSession;

  return {
    createCheckoutSession,
    handleCheckout,
    isLoading,
    isProcessing: isLoading,
    error,
    debugInfo
  };
};
